import { useRef, useState } from "react";

import { toCount } from "common/utils/formatting";
import { toggleValue } from "common/utils/sets";
import { IAddress } from "common/types";
import CatalogImage from "common/components/CatalogImage";
import { deepCombineErrors } from "common/utils/errors";
import { scrollOverflowAncestorToTop } from "common/utils/dom";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { BasicSubscription } from "client/subscriptions/types";
import {
  usePaginatedBasicSubscriptionsQuery,
  useUpdateBulkSubscriptionsMutation,
} from "client/subscriptions/hooks";
import { NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES } from "client/subscriptions/constants";
import Checkbox from "client/common/components/inputs/Checkbox";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import Pagination from "client/common/components/layouts/Pagination";

import styles from "../../styles/SubscriptionBulkUpdateAddressDrawer.module.less";

export type SubscriptionsBulkUpdateAddressDrawerProps = {
  address: IAddress;
};

const SubscriptionBulkUpdateAddressDrawer = ({
  address,
}: SubscriptionsBulkUpdateAddressDrawerProps) => {
  const { close } = useCurrentDrawer();
  const listRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [excludedSubscriptionIds, setExcludedSubscriptionIds] = useState<Set<number>>(new Set());
  const {
    mutate: bulkUpdate,
    isPending: isLoading,
    error,
  } = useUpdateBulkSubscriptionsMutation({
    onSuccess: close,
  });
  const {
    data,
    isLoading: isSubscriptionsLoading,
    error: subscriptionsError,
  } = usePaginatedBasicSubscriptionsQuery({
    visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES,
    exclude_shipping_address_id: address.id,
    page,
  });

  const subscriptions = data?.results ?? [];
  const totalCount = data?.paginator.count ?? 0;
  const selectedCount = totalCount - excludedSubscriptionIds.size;
  const canSubmit = selectedCount > 0;

  const onSubmit = () => {
    bulkUpdate({
      shipping_address_id: address.id,
      visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES,
      exclude_shipping_address_id: address.id,
      ...(excludedSubscriptionIds.size > 0
        ? { exclude_subscription_ids: [...excludedSubscriptionIds] }
        : {}),
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    scrollOverflowAncestorToTop(listRef.current);
  };

  if (isSubscriptionsLoading && !data) {
    return <DrawerLayout.Loading />;
  }

  if (subscriptionsError || !data) {
    return (
      <DrawerLayout.FetchError entityName="autoship" isPlural={true} error={subscriptionsError} />
    );
  }

  const modalLayoutHeader = (
    <div className={styles.nameContainer}>
      You have Autoships with another address on file. Select the Autoships you would like to update
      to the new address:
      <div className={styles.nameContainer}>
        <div className={styles.name}>
          <b>{address.address_one}</b>
        </div>
      </div>
    </div>
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update your Autoships"
        description={modalLayoutHeader}
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={canSubmit}
        onSubmit={onSubmit}
        submitText={`Update ${toCount(selectedCount, "Autoship")}`}
        isLoading={isLoading}
        error={error}
        fieldErrors={[deepCombineErrors(error?.errors)]}
      >
        <div ref={listRef} className={styles.list}>
          {subscriptions.map((subscription: BasicSubscription) => (
            <DrawerLayout.Form.Group key={subscription.id}>
              <div className={styles.option}>
                <Checkbox
                  value={!excludedSubscriptionIds.has(subscription.id)}
                  onChange={() =>
                    setExcludedSubscriptionIds(
                      toggleValue(excludedSubscriptionIds, subscription.id),
                    )
                  }
                />
                <div className={styles.item}>
                  <CatalogImage
                    image={subscription.item.image}
                    size="thumbnail"
                    alt={subscription.item.full_name}
                    className={styles.image}
                  />
                  <div className={styles.nameContainer}>
                    <div className={styles.name}>
                      {subscription.item.product_line_name} {subscription.item.name} for{" "}
                      <b>{subscription.patient.name_display}</b>
                    </div>
                    {subscription.shipping_address && (
                      <div className={styles.name}>
                        Currently ships to:
                        <div className={styles.name}>
                          <b>{subscription.shipping_address.address_one}</b>
                        </div>
                        {!!subscription.shipping_address.address_two && (
                          <div className={styles.name}>
                            <b>{subscription.shipping_address.address_two}</b>
                          </div>
                        )}
                        <div className={styles.name}>
                          <b>
                            {subscription.shipping_address.city},{" "}
                            {subscription.shipping_address.state}{" "}
                            {subscription.shipping_address.zipcode}
                          </b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DrawerLayout.Form.Group>
          ))}
          <Pagination
            paginator={data.paginator}
            onPageChange={handlePageChange}
            entityName="Autoship"
          />
        </div>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionBulkUpdateAddressDrawer;
